// Shared Escape-to-clear behavior for text inputs. Several of these inputs
// live inside a ModalShell, whose Escape-to-close listener is attached at the
// document level (see useEscapeToClose.ts) — stopping propagation here means
// Escape clears the text first, and only closes the modal on a second press
// once the field is already empty.
import { useEffect, type KeyboardEvent as ReactKeyboardEvent } from "react";

const CLEARABLE_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "url",
  "tel",
  "password",
]);

export function isClearableField(
  el: EventTarget | null,
): el is HTMLInputElement | HTMLTextAreaElement {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.readOnly || el.disabled) return false;
  return CLEARABLE_INPUT_TYPES.has(el.type);
}

/** Sets a controlled input/textarea's value the way a user would, so React's
 *  onChange runs. Used by the document-level Escape handler (which cannot see
 *  the React setter) and by the in-field clear button. */
export function setNativeInputValue(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string,
): void {
  const proto =
    el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  setter?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export function clearOnEscape(value: string, setValue: (v: string) => void) {
  return (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Escape" && value) {
      e.stopPropagation();
      e.preventDefault();
      setValue("");
    }
  };
}

/** Site-wide Escape-to-clear: while focus is in a text field that has a value,
 *  the first Escape clears it and does not close an enclosing modal. A second
 *  Escape (field already empty) propagates normally. Capture-phase so this
 *  runs before useEscapeToClose's document bubble listener. */
export function useGlobalEscapeToClear(): void {
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key !== "Escape") return;
      const el = e.target;
      if (!isClearableField(el) || !el.value) return;
      e.stopPropagation();
      e.preventDefault();
      e.stopImmediatePropagation();
      setNativeInputValue(el, "");
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);
}
